/**
 * Module dependencies.
 */

import {
  Box,
  RawHtml,
  media,
  units,
  useBreakpoint
} from '@untile/react-components';

import { Button, Image, Subtitle, Type } from 'src/components/core';
import { FavoriteProps } from 'src/types/my-products';
import { ReactElement, forwardRef, useEffect } from 'react';
import { TechnologyProps } from 'src/types/technologies';
import { fixRegisteredTrademarkSymbol } from 'src/core/utils/strings';
import { prop, theme } from 'styled-tools';
import { useInView } from 'react-intersection-observer';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useSectionsNavigation } from 'src/context/sections-navigation/context';
import ButtonsWrapper from './buttons-wrapper';
import Container from 'src/components/core/layout/container';
import List from 'src/components/list';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = Pick<
  TechnologyProps,
  | 'advantagesList'
  | 'approvalsList'
  | 'awardsImages'
  | 'bgColor'
  | 'brochureFile'
  | 'description'
  | 'featuredImage'
  | 'gallery'
  | 'id'
  | 'slug'
  | 'subtitle'
  | 'technologyList'
  | 'title'
> & {
  className?: string;
  existingFavorite: FavoriteProps | null;
  hasCustomizationsButton?: boolean;
  onGoToModelRangesSection?: () => void;
  refetchExistingFavorite: () => void;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  padding-bottom: ${units(3)};
  position: relative;

  ${media.min('md')`
    padding-bottom: ${units(17.5)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'background background     background'
    '.          image          .'
    '.          title          .'
    '.          subtitle       .'
    '.          buttons        .'
    '.          buttons        .'
    '.          description    .'
    '.          detailsWrapper .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme(
      'grid.gutterMobile'
    )}px;
  grid-template-rows:
    ${theme('dimensions.navbarHeightMobile')}px repeat(3, max-content)
    46px repeat(3, max-content);

  ${media.min('md')`
    grid-template-areas:
      '. .     . . background     .'
      '. image . . .              .'
      '. image . . title          .'
      '. image . . subtitle       .'
      '. image . . description    .'
      '. image . . buttons        .'
      '. image . . detailsWrapper .'
      '. image . . detailsWrapper .';
    grid-template-columns: 0.7fr 5.3fr 1fr ${theme(
      'grid.columnsGutter'
    )}px 4fr 1fr;
    grid-template-rows: ${theme('dimensions.navbarHeight')}px ${units(
    10
  )} repeat(4, max-content) ${units(6.5)} max-content;
  `}
`;

/**
 * `Background` styled component.
 */

const Background = styled.div<{ bgColor: string }>`
  background-color: ${prop('bgColor')};
  grid-area: background;
  grid-column: 1 / -1;
  grid-row: 1 / 6;
  position: relative;
  z-index: -1;

  ${media.min('md')`
    grid-row: 1 / 8;
  `}
`;

/**
 * `ImageOuterWrapper` styled component.
 */

const ImageOuterWrapper = styled.div`
  grid-area: image;
  position: relative;

  ${media.max('md')`
    margin-bottom: ${units(5)};
  `}
`;

/**
 * `ImageGalleryWrapper` styled component.
 */

const ImageGalleryWrapper = styled.div`
  text-align: center;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  margin-bottom: ${units(1)};
  overflow: hidden;
  position: relative;

  ${media.max('md')`
    margin: 0 auto;
    max-width: ${units(60)};
    width: 100%;
  `}

  ${media.min('lg')`
    align-self: start;
    margin-bottom: ${units(5.5)};
  `}
`;

/**
 * `DetailsWrapper` styled component.
 */

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: detailsWrapper;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;
`;

/**
 * `SubtitleText` styled component.
 */

const SubtitleText = styled(Subtitle).attrs({ size: 'large' })`
  grid-area: subtitle;
  margin-bottom: ${units(2)};

  ${media.min('md')`
    margin-bottom: ${units(4)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  grid-area: description;
  margin-bottom: ${units(3)};

  ${media.min('md')`
    margin-bottom: ${units(4)};
  `}
`;

/**
 * `ListLabel` styled component.
 */

const ListLabel = styled(Type.H3)`
  margin-bottom: ${units(2)};
`;

/**
 * `AwardsImagesWrapper` styled component.
 */

const AwardsImagesWrapper = styled.div`
  align-items: center;
  display: grid;
  grid-auto-columns: ${units(7)};
  grid-gap: ${units(2)};
`;

/**
 * `Header` component.
 */

const Header = (props: Props, sectionRef: any): ReactElement => {
  const {
    advantagesList,
    approvalsList,
    awardsImages,
    bgColor,
    brochureFile,
    className,
    description,
    existingFavorite,
    featuredImage,
    gallery,
    hasCustomizationsButton,
    id,
    onGoToModelRangesSection,
    refetchExistingFavorite,
    slug,
    subtitle,
    technologyList,
    title
  } = props;

  const { translate } = useTranslate();
  const isMobile = useBreakpoint('md', 'max');
  const routeResolver = useLocalizedRoute();
  const { onSetCurrentSection } = useSectionsNavigation();
  const [ref, inView] = useInView({ threshold: 0.5 });
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  useEffect(() => {
    if (inView) {
      onSetCurrentSection('overview');
    }
  }, [inView, onSetCurrentSection]);

  return (
    <Wrapper
      className={className}
      ref={sectionRef}
    >
      <StyledContainer ref={ref}>
        <Grid id={'grid'}>
          <Background bgColor={bgColor} />

          <ImageOuterWrapper>
            <ImageGalleryWrapper
              {...(!isMobile && {
                'data-scroll': true,
                'data-scroll-sticky': true,
                'data-scroll-target': '#grid'
              })}
            >
              <div
                {...(!isMobile && {
                  'data-scroll': true,
                  'data-scroll-speed': 2
                })}
              >
                <ImageWrapper key={featuredImage}>
                  <Image
                    alt={title}
                    height={763}
                    layout={isMobile ? 'responsive' : 'intrinsic'}
                    src={featuredImage}
                    style={{ objectFit: 'contain' }}
                    width={640}
                  />
                </ImageWrapper>

                {size(gallery) > 0 && (
                  <Button
                    colorTheme={'tertiary'}
                    href={routeResolver('technologyGallery', { slug })}
                    size={'small'}
                  >
                    {translate('common:actions.openGallery')}
                  </Button>
                )}
              </div>
            </ImageGalleryWrapper>
          </ImageOuterWrapper>

          <RawHtml
            {...animProps}
            element={Title}
          >
            {fixRegisteredTrademarkSymbol(title)}
          </RawHtml>

          <SubtitleText {...animProps}>{subtitle}</SubtitleText>

          <Box
            gridArea={'buttons'}
            marginBottom={units(6)}
            {...animProps}
          >
            <ButtonsWrapper
              contactUrl={routeResolver('technologyDetailsContact', { slug })}
              existingFavorite={existingFavorite}
              hasCustomizationsButton={hasCustomizationsButton}
              id={id}
              onGoToModelRangesSection={onGoToModelRangesSection}
              refetchExistingFavorite={refetchExistingFavorite}
              slug={slug}
              type={'technology'}
            />
          </Box>

          <Description
            data-scroll={'fade-in-up-small'}
            data-scroll-offset={'20%'}
          >
            {description}
          </Description>

          <DetailsWrapper>
            <Box
              data-scroll={'fade-in-up-small'}
              data-scroll-offset={'20%'}
              marginBottom={units(4)}
            >
              <List
                items={technologyList}
                label={translate('technologies:technology')}
              />
            </Box>

            <Box
              data-scroll={'fade-in-up-small'}
              data-scroll-offset={'20%'}
              marginBottom={units(4)}
            >
              <List
                items={advantagesList}
                label={translate('technologies:advantages')}
              />
            </Box>

            {!isEmpty(approvalsList) && (
              <Box
                data-scroll={'fade-in-up-small'}
                data-scroll-offset={'20%'}
                marginBottom={units(4)}
              >
                <List
                  items={approvalsList}
                  label={translate('technologies:approvals')}
                />
              </Box>
            )}

            {!isEmpty(awardsImages) && (
              <Box
                marginBottom={units(6)}
                {...animProps}
              >
                <ListLabel>{translate('technologies:awards')}</ListLabel>

                <AwardsImagesWrapper>
                  {map(awardsImages, (imageUrl, index) => (
                    <Image
                      alt={imageUrl}
                      height={40}
                      key={index}
                      src={imageUrl}
                      style={{
                        objectFit: 'scale-down',
                        objectPosition: 'left'
                      }}
                      width={56}
                    />
                  ))}
                </AwardsImagesWrapper>
              </Box>
            )}

            {brochureFile && (
              <Box {...animProps}>
                <Button
                  colorTheme={'secondary'}
                  href={brochureFile}
                  size={'medium'}
                >
                  {translate('common:actions.downloadBrochure')}
                </Button>
              </Box>
            )}
          </DetailsWrapper>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `Header` component.
 */

export default forwardRef(Header);
